import { hasValidPublicUserIdentity, publicUserId } from "./public-user-identity.mjs";

const CHALLENGE_ID_PATTERN = /^otp_[A-Za-z0-9_-]{16,128}$/;
const COMPLETION_SECRET_PATTERN = /^[A-Za-z0-9_-]{32,512}$/;
const BASE_RESPONSE_KEYS = new Set([
  "ok",
  "expiresInSec",
  "reissued",
  "identityType",
  "deliveryChannel",
  "primaryDeliveryChannel",
  "fallbackDeliveryChannel",
  "whatsappFallbackAvailable",
  "whatsappFallbackEngaged",
  "whatsappFallbackAutomatic",
  "voiceFallbackAvailable",
  "voiceFallbackEngaged",
  "whatsappVerificationMode",
  "deliveryStatus",
  "challengeId",
  "completionSecret",
  "completionMode",
]);
const LINK_RESPONSE_KEYS = new Set([...BASE_RESPONSE_KEYS, "approvalState", "pollAfterMs"]);
const PENDING_STATUS_KEYS = new Set([
  "ok",
  "purpose",
  "approvalState",
  "expiresInSec",
  "pollAfterMs",
]);
const APPROVED_STATUS_KEYS = new Set(["ok", "purpose", "approvalState", "expiresInSec"]);
const CONFIRMATION_KEYS = new Set(["ok", "passwordChanged", "user"]);

function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasOnlyKeys(record, allowed) {
  const keys = Object.keys(record);
  return keys.length === allowed.size && keys.every((key) => allowed.has(key));
}

function isPositiveNumber(value) {
  return Number.isFinite(value) && value > 0;
}

function invalidPasswordChangeResponse() {
  const error = new Error("invalid_password_change_response");
  error.code = "invalid_password_change_response";
  return error;
}

/**
 * Mirrors the server's destination-authoritative contact selection. This is
 * used only to fail closed when a response contradicts the signed-in account;
 * the server remains the routing authority.
 */
export function expectedPasswordChangeDelivery(user) {
  if (typeof user?.email === "string" && user.email.trim() && user.emailVerified === true) {
    return Object.freeze({ identityType: "email", deliveryChannel: "email" });
  }
  const phone = String(user?.phoneE164 || "").trim();
  if (phone) {
    return Object.freeze({
      identityType: "phone",
      deliveryChannel: phone.startsWith("+91") ? "whatsapp" : "sms",
    });
  }
  if (typeof user?.email === "string" && user.email.trim()) {
    return Object.freeze({ identityType: "email", deliveryChannel: "email" });
  }
  return null;
}

function isExactEmailLane(value) {
  return (
    value.identityType === "email" &&
    value.deliveryChannel === "email" &&
    value.primaryDeliveryChannel === "email" &&
    value.fallbackDeliveryChannel === null &&
    value.whatsappVerificationMode === "code" &&
    value.completionMode === "otp_code" &&
    value.deliveryStatus === "submitted" &&
    value.voiceFallbackAvailable === false &&
    value.voiceFallbackEngaged === false
  );
}

function isExactSmsLane(value) {
  return (
    value.identityType === "phone" &&
    value.deliveryChannel === "sms" &&
    value.primaryDeliveryChannel === "sms" &&
    value.fallbackDeliveryChannel === null &&
    value.whatsappVerificationMode === "code" &&
    value.completionMode === "otp_code" &&
    value.deliveryStatus === "submitted" &&
    value.voiceFallbackAvailable === false &&
    value.voiceFallbackEngaged === false
  );
}

function isExactVoiceLane(value) {
  return (
    value.identityType === "phone" &&
    value.deliveryChannel === "voice" &&
    value.primaryDeliveryChannel === "whatsapp" &&
    value.fallbackDeliveryChannel === "voice" &&
    value.whatsappVerificationMode === "code" &&
    value.completionMode === "otp_code" &&
    value.deliveryStatus === "submitted" &&
    value.voiceFallbackAvailable === false &&
    value.voiceFallbackEngaged === true
  );
}

function isExactWhatsAppLinkLane(value) {
  return (
    value.identityType === "phone" &&
    value.deliveryChannel === "whatsapp" &&
    value.primaryDeliveryChannel === "whatsapp" &&
    value.fallbackDeliveryChannel === "voice" &&
    value.whatsappVerificationMode === "account_link" &&
    value.completionMode === "link_approval" &&
    (value.deliveryStatus === "submitted" || value.deliveryStatus === "unknown") &&
    typeof value.voiceFallbackAvailable === "boolean" &&
    value.voiceFallbackEngaged === false &&
    value.approvalState === "pending" &&
    isPositiveNumber(value.pollAfterMs)
  );
}

export function readPasswordChangeChallenge(value, expected) {
  const linkMode = isRecord(value) && value.completionMode === "link_approval";
  const exactKeys =
    isRecord(value) && hasOnlyKeys(value, linkMode ? LINK_RESPONSE_KEYS : BASE_RESPONSE_KEYS);
  const exactLane =
    isRecord(value) &&
    (isExactEmailLane(value) ||
      isExactSmsLane(value) ||
      isExactVoiceLane(value) ||
      isExactWhatsAppLinkLane(value));
  const valid =
    isRecord(value) &&
    exactKeys &&
    exactLane &&
    value.ok === true &&
    typeof value.reissued === "boolean" &&
    isPositiveNumber(value.expiresInSec) &&
    CHALLENGE_ID_PATTERN.test(String(value.challengeId || "")) &&
    COMPLETION_SECRET_PATTERN.test(String(value.completionSecret || "")) &&
    value.whatsappFallbackAvailable === false &&
    value.whatsappFallbackEngaged === false &&
    value.whatsappFallbackAutomatic === false &&
    value.identityType === expected?.identityType &&
    value.deliveryChannel === expected?.deliveryChannel;
  if (!valid) throw invalidPasswordChangeResponse();
  return value;
}

export function readPasswordChangeApprovalStatus(value) {
  const pending =
    isRecord(value) &&
    hasOnlyKeys(value, PENDING_STATUS_KEYS) &&
    value.ok === true &&
    value.purpose === "password_change" &&
    value.approvalState === "pending" &&
    isPositiveNumber(value.expiresInSec) &&
    isPositiveNumber(value.pollAfterMs);
  if (pending) return value;

  const approved =
    isRecord(value) &&
    hasOnlyKeys(value, APPROVED_STATUS_KEYS) &&
    value.ok === true &&
    value.purpose === "password_change" &&
    value.approvalState === "approved" &&
    isPositiveNumber(value.expiresInSec);
  if (approved) return value;
  throw invalidPasswordChangeResponse();
}

export function readPasswordChangeConfirmation(
  value,
  expectedUserId,
  expectedVerificationIdentityType,
) {
  const returnedUserId = isRecord(value) ? publicUserId(value.user) : "";
  const valid =
    isRecord(value) &&
    hasOnlyKeys(value, CONFIRMATION_KEYS) &&
    value.ok === true &&
    value.passwordChanged === true &&
    Boolean(String(expectedUserId || "").trim()) &&
    returnedUserId === String(expectedUserId || "").trim() &&
    hasValidPublicUserIdentity(value.user) &&
    value.user.verificationIdentityType === expectedVerificationIdentityType;
  if (!valid) throw invalidPasswordChangeResponse();
  return value.user;
}
